var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Box, Button } from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { getModalSummaryPresenter } from "../../../../application/factories/modalSummaryPresenter.factory";
import { useFormValidationStore } from "../../../../application/stores/formValidation.store";
import GenericModal from "../../../../components/common/Modals/GenericModal/GenericModal";
import { AnswerProcessorService, FormEvaluatorService, PDDAnswerRulesService } from "../../../../infrastructure/services";
import { ActivityService } from "../../../../infrastructure/services/activity.impl.service";
import { BinaryInput } from "./BinaryInput";
import { MultiSelectInput } from "./MultiSelectInput";
import { SingleChoiceInput } from "./SingleChoiceInput";
import { TextInput } from "./TextInput";
export const ModalFormInput = ({ question, value, onChange, onModalSubmit, disabled, translator, details, error, infoModal, isReadMode = false }) => {
    var _a, _b, _c, _d;
    const [open, setOpen] = useState(false);
    const [modalData, setModalData] = useState({});
    const summaryPresenter = getModalSummaryPresenter((_a = question === null || question === void 0 ? void 0 : question.modal) === null || _a === void 0 ? void 0 : _a.summaryComponent);
    const [dynamicOptions, setDynamicOptions] = useState([]);
    const processor = new AnswerProcessorService();
    const evaluator = new FormEvaluatorService();
    const { validateField, errors } = useFormValidationStore();
    const isMultiple = question.multiple === true;
    // Ref que guarda el Set de nombres de campos visibles en el render anterior
    const prevVisibleRef = useRef(new Set());
    // Convertimos `details` en un array de entradas
    const entries = Array.isArray(details)
        ? details
        : details && typeof details === "object"
            ? [details]
            : [];
    // Valor base de la pregunta binaria
    const baseValue = typeof value === "string" ? value : "";
    // --------------------------------------------------------
    // 1) Cálculo de validez del modal: SOLO campos visibles
    // --------------------------------------------------------
    const isModalValid = useMemo(() => {
        if (!question.modal)
            return true;
        // Filtramos los campos que están visibles según sus displayConditions
        const visibleFields = question.modal.fields.filter(f => {
            if (!f.displayConditions)
                return true;
            return evaluator.evaluateDisplayConditions(f.displayConditions, modalData, {});
        });
        // Ahora comprobamos, de esos campos visibles, que cumplan mandatory + validaciones
        return visibleFields.every(f => {
            const val = modalData[f.variableName];
            // Si es mandatory, debe existir y no estar vacío
            if (f.mandatory &&
                (val === undefined ||
                    val === null ||
                    val === "" ||
                    (Array.isArray(val) && val.length === 0))) {
                return false;
            }
            // Si tiene validación, aplicarla
            if (f.validation) {
                const { type, length, regex } = f.validation;
                const strVal = String(val !== null && val !== void 0 ? val : "");
                switch (type) {
                    case "length":
                        if (typeof length === "number" && strVal.length > length) {
                            return false;
                        }
                        break;
                    case "custom-regex": {
                        const pattern = typeof regex === "string" ? new RegExp(regex) : regex;
                        if (!pattern.test(strVal)) {
                            return false;
                        }
                        break;
                    }
                    case "email":
                        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(strVal)) {
                            return false;
                        }
                        break;
                    case "phone":
                        if (isNaN(Number(val))) {
                            return false;
                        }
                        break;
                }
            }
            return true;
        });
    }, [modalData, question.modal, evaluator]);
    // --------------------------------------------------------
    // Cargar opciones dinámicas si existe dataSource
    useEffect(() => {
        var _a;
        const fieldWithDS = (_a = question.modal) === null || _a === void 0 ? void 0 : _a.fields.find(f => !!f.dataSource);
        if (!fieldWithDS) {
            return;
        }
        const fetchData = () => __awaiter(void 0, void 0, void 0, function* () {
            const svc = new ActivityService();
            const methodName = fieldWithDS.dataSource.method;
            const items = (yield svc[methodName]());
            const opts = items.map(item => ({
                value: item.name,
                label: item.name,
            }));
            setDynamicOptions(opts);
        });
        fetchData();
    }, [(_b = question.modal) === null || _b === void 0 ? void 0 : _b.fields]);
    // Cada vez que cambian los datos del modal, revalidamos sus campos
    useEffect(() => {
        var _a;
        (_a = question.modal) === null || _a === void 0 ? void 0 : _a.fields.forEach(f => {
            var _a;
            if (f.validation) {
                const val = (_a = modalData[f.variableName]) !== null && _a !== void 0 ? _a : "";
                validateField(f.variableName, val, f.validation);
            }
        });
    }, [modalData, question.modal, validateField]);
    // --------------------------------------------------------
    // 2) Efecto para borrar de modalData las respuestas de campos que dejan de ser visibles
    // --------------------------------------------------------
    useEffect(() => {
        if (!question.modal)
            return;
        // Calculamos el conjunto de variableName de campos VISIBLES en este render
        const currentVisible = new Set();
        question.modal.fields.forEach(f => {
            if (!f.displayConditions) {
                currentVisible.add(f.variableName);
            }
            else {
                const isVisible = evaluator.evaluateDisplayConditions(f.displayConditions, modalData, {});
                if (isVisible) {
                    currentVisible.add(f.variableName);
                }
            }
        });
        // Comparamos con el conjunto anterior
        const prevVisible = prevVisibleRef.current;
        const justHidden = [];
        prevVisible.forEach(varName => {
            if (!currentVisible.has(varName)) {
                justHidden.push(varName);
            }
        });
        if (justHidden.length > 0) {
            setModalData(prev => {
                const copy = Object.assign({}, prev);
                justHidden.forEach(varName => {
                    delete copy[varName];
                });
                return copy;
            });
        }
        // Actualizamos el ref para el siguiente render
        prevVisibleRef.current = currentVisible;
    }, [modalData, question.modal, evaluator]);
    // --------------------------------------------------------
    const handleFieldChange = (name, val) => {
        var _a;
        const field = (_a = question.modal) === null || _a === void 0 ? void 0 : _a.fields.find(f => f.variableName === name);
        if (!field)
            return;
        let newValue = val;
        // Si es multipleChoice, procesar array
        if (field.type === "multipleChoice" && Array.isArray(val)) {
            const prev = modalData[name] || [];
            const diff = val.find(v => !prev.includes(v)) || prev.find(v => !val.includes(v));
            newValue = processor.processMultiSelectAnswer(field, prev, diff);
        }
        else if ((field.type === "singleChoice" || field.type === "binaryChoice") &&
            typeof val === "string") {
            newValue = val;
        }
        setModalData(prev => (Object.assign(Object.assign({}, prev), { [name]: newValue })));
    };
    const handleSubmit = () => {
        const targetKey = question.variableName + "_details";
        const currentDetails = Array.isArray(details) ? details : details ? [details] : [];
        const newValue = isMultiple ? [...currentDetails, modalData] : modalData;
        const allEntries = isMultiple ? [...currentDetails, modalData] : [modalData];
        // Reglas derivadas
        const derived = new PDDAnswerRulesService().evaluateDerivedAnswersFromModalEntries(question, allEntries);
        onChange(question.variableName, "1");
        onChange(targetKey, newValue);
        Object.entries(derived).forEach(([k, v]) => onChange(k, v));
        onModalSubmit(modalData);
        setModalData({});
        setOpen(false);
    };
    const handleRemove = (index) => {
        const updated = entries.filter((_, i) => i !== index);
        onChange(question.variableName + "_details", updated);
        // Recalcular reglas derivadas con las entradas actualizadas
        const answerRules = new PDDAnswerRulesService();
        const derived = answerRules.evaluateDerivedAnswersFromModalEntries(question, updated);
        Object.entries(derived).forEach(([k, v]) => onChange(k, v));
    };
    const handleCloseModal = () => {
        setOpen(false);
        setModalData({});
    };
    const detailKey = question.variableName + "_details";
    const detailErrorMsg = useFormValidationStore().errors[detailKey];
    const showDetailError = value === "1" && !!detailErrorMsg;
    return (_jsxs(_Fragment, { children: [_jsx(BinaryInput, Object.assign({ shouldShowYes: !isReadMode, isReadMode: isReadMode, label: translator.getQuestionText(question.translationKey), options: question.answers.map(a => ({
                    label: translator.getAnswerLabel(a.translationKey),
                    value: a.value,
                })), value: baseValue, onChange: val => {
                    onChange(question.variableName, val);
                    const targetKey = question.variableName + "_details";
                    if (val === "0") {
                        // Al desmarcar, borramos detalles del modal
                        onChange(targetKey, isMultiple ? [] : null);
                    }
                }, disabled: disabled, error: showDetailError }, (question.infoModal ? { infoModal: question.infoModal } : {}))), entries.length > 0 && summaryPresenter && (_jsx(Box, Object.assign({ mt: 2 }, { children: isMultiple && summaryPresenter.renderGroup
                    ? summaryPresenter.renderGroup(entries, handleRemove, isReadMode)
                    : entries.map((entry, index) => (_jsx("div", { children: summaryPresenter.render(entry, () => handleRemove(index), index, isReadMode) }, index))) }))), !isReadMode && value === "1" && (isMultiple || entries.length === 0) && (_jsx(Box, Object.assign({ mt: 1 }, { children: _jsxs(Button, Object.assign({ color: "error", onClick: () => setOpen(true), disabled: disabled }, { children: ["+ ", translator.getAnswerLabel(question.addButtonTranslationKey)] })) }))), _jsx(GenericModal, Object.assign({ open: open, title: translator.getQuestionText((_c = question.modal) === null || _c === void 0 ? void 0 : _c.titleTranslationKey), cssProps: { width: "575px" }, primaryAction: _jsx(Button, Object.assign({ onClick: handleSubmit, variant: "contained", disabled: !isModalValid }, { children: "Aceptar" })), secondaryAction: _jsx(Button, Object.assign({ onClick: handleCloseModal }, { children: "Cancelar" })) }, { children: _jsx("div", Object.assign({ className: "container-fluid ps-0 modal-PDDForm" }, { children: _jsx("div", Object.assign({ className: "row" }, { children: (_d = question.modal) === null || _d === void 0 ? void 0 : _d.fields.map(f => {
                            const shouldShowField = !f.displayConditions ||
                                evaluator.evaluateDisplayConditions(f.displayConditions, modalData, {});
                            if (!shouldShowField) {
                                return null;
                            }
                            const { variableName, labelTranslationKey, type, answers, dataSource, infoModal, col, subSection, clearBoth } = f;
                            const errorMsg = errors[variableName] || "";
                            // Props comunes para todos los inputs
                            const commonProps = {
                                label: translator.getQuestionText(labelTranslationKey),
                                value: type === "multipleChoice"
                                    ? (Array.isArray(modalData[variableName])
                                        ? modalData[variableName]
                                        : [])
                                    : modalData[variableName] || "",
                                onChange: (v) => handleFieldChange(variableName, v),
                                disabled: false,
                                error: !!errorMsg,
                                helperText: errorMsg,
                            };
                            switch (type) {
                                case "text":
                                    return (_jsxs(React.Fragment, { children: [subSection && _jsx("h2", Object.assign({ className: "fs-20 fw-600 grid-mb-1 grid-mt-1" }, { children: translator.getSubSectionTitle(subSection) })), _jsx("div", Object.assign({ className: `col-12  grid-mb-1 ${col ? `col-md-${col}` : ''}` }, { children: _jsx(TextInput, Object.assign({}, commonProps, (infoModal ? { infoModal } : {}))) })), clearBoth && _jsx("div", { className: "w-100" })] }, variableName));
                                case "singleChoice":
                                    const opts = dataSource
                                        ? dynamicOptions
                                        : answers.map(a => ({
                                            label: translator.getAnswerLabel(a.translationKey),
                                            value: a.value,
                                        }));
                                    return (_jsxs(React.Fragment, { children: [subSection && _jsx("h2", Object.assign({ className: "fs-20 fw-600 grid-mb-1 grid-mt-1" }, { children: translator.getSubSectionTitle(subSection) })), _jsx("div", Object.assign({ className: `col-12  grid-mb-1 ${col ? `col-md-${col}` : ''}` }, { children: _jsx(SingleChoiceInput, Object.assign({}, commonProps, { options: opts }, (infoModal ? { infoModal } : {}))) })), clearBoth && _jsx("div", { className: "w-100" })] }, variableName));
                                case "binaryChoice":
                                    return (_jsxs(React.Fragment, { children: [subSection && _jsx("h2", Object.assign({ className: "fs-20 fw-600 grid-mb-1 grid-mt-1" }, { children: translator.getSubSectionTitle(subSection) })), _jsx("div", Object.assign({ className: `col-12 grid-mb-1 ${col ? `col-md-${col}` : ''}` }, { children: _jsx(BinaryInput, Object.assign({}, commonProps, (infoModal ? { infoModal } : {}), { options: answers.map(a => ({
                                                        label: translator.getAnswerLabel(a.translationKey),
                                                        value: a.value,
                                                    })) })) })), clearBoth && _jsx("div", { className: "w-100" })] }, variableName));
                                case "multipleChoice":
                                    return (_jsxs(React.Fragment, { children: [subSection && _jsx("h2", Object.assign({ className: "fs-20 fw-600 grid-mb-1 grid-mt-1" }, { children: translator.getSubSectionTitle(subSection) })), _jsx("div", Object.assign({ className: `col-12  grid-mb-1 ${col ? `col-md-${col}` : ''}` }, { children: _jsx(MultiSelectInput, Object.assign({}, commonProps, (infoModal ? { infoModal } : {}), { options: answers.map(a => ({
                                                        label: translator.getAnswerLabel(a.translationKey),
                                                        value: a.value,
                                                    })) })) })), clearBoth && _jsx("div", { className: "w-100" })] }, variableName));
                                default:
                                    return null;
                            }
                        }) })) })) }))] }));
};
